/* eslint-disable no-param-reassign */
import type { I3DProperty, IBevel3D, IRotation3D, IScene3D, IShape3D } from './property3d.interface';
import type { IShapeBaseModel, IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import type { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import type { CameraPresetType, LightType, MaterialType } from '@tencent/docs-renderer-3d';
import type { IBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import type { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import type { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import type { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import type { ICTShape3DDelta, IDrawingShapeDataModel } from '@tencent/slide-sdk-model';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { enumerateGroup, enumerateGroupItems } from '@src/slide/domain/ui-model/element-modeles/property/group-operation/enumerate';
import { recurseChildModels } from '@src/slide/domain/ui-model/element-modeles/property/utils/merge-property';
import { transaction } from '@src/slide/domain/slide-app';
import { mergeObjects } from '@src/slide/infrastructure/module/independent/object/merge';
import { Element3DConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/3d/element-3d-converter';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';

type IModelWithScene3d = IGroupModel | IShapeBaseModel;

function isGroupModel(model: IBaseModel): model is IGroupModel {
    return model.type === UIModelType.GROUP;
}

function isShapeBaseModel(model: IBaseModel): model is IElementBaseModel {
    return model.type === UIModelType.SHAPE;
}

function isShapeModel(model: IBaseModel): model is IShapeModel {
    return model.type === UIModelType.SHAPE;
}

function isPictureModel(model: IBaseModel): model is IPictureModel {
    return model.type === UIModelType.IMAGE;
}

function isMultiplyModel(model: IBaseModel): model is IMultiplyModel {
    return model.type === UIModelType.MULTIPLY;
}

export default class ThreeDProperty implements I3DProperty {
    public constructor(public readonly model: IElementBaseModel | IMultiplyModel) {}

    public reset3dProperties(): void {
        this.handleMultiplyModel(this.resetChild3dProperties);
    }

    public updateShape3D(shape3d: Partial<IShape3D> | undefined): void {
        this.handleMultiplyModel(this.updateChildShape3D, shape3d);
    }

    public updateScene3D(scene3d: Partial<IScene3D> | undefined): void {
        this.handleMultiplyModel(this.updateChildScene3D, scene3d);
    }

    public updateExtrusionHeight(extrusionHeight: number): void {
        this.handleMultiplyModel(this.updateChildExtrusionHeight, extrusionHeight);
    }

    public updateExtrusionColor(color: string): void {
        this.handleMultiplyModel(this.updateChildExtrusionColor, color);
    }

    public updateIsFlatText(isFlatText: boolean): void {
        this.handleMultiplyModel(this.updateChildIsFlatText, isFlatText);
    }

    public updateCameraRotation(rotation: Partial<IRotation3D>): void {
        this.handleMultiplyModel(this.updateChildCameraRotation, rotation);
    }

    public updateLightRotation(rotation: Partial<IRotation3D>): void {
        this.handleMultiplyModel(this.updateChildLightRotation, rotation);
    }

    public updateLightRigPreset(rigPreset: string): void {
        this.handleMultiplyModel(this.updateChildLightRigPreset, rigPreset);
    }

    public updateMaterialPreset(materialPreset: string): void {
        this.handleMultiplyModel(this.updateChildMaterialPreset, materialPreset);
    }

    public updateCameraPreset(cameraPreset: string): void {
        this.handleMultiplyModel(this.updateChildCameraPreset, cameraPreset);
    }

    public getMultiplyScene3D(): IScene3D | undefined {
        if (!isMultiplyModel(this.model)) {
            return undefined;
        }
        if (this.model.selectedModels.length <= 1) {
            return this.model.selectedModels[0]?.scene3d;
        }
        const result = this.model.selectedModels.slice(1).reduce((
            multiplyScene3d,
            child,
        ) => mergeObjects(multiplyScene3d, child.scene3d), this.model.selectedModels[0].scene3d);
        return result;
    }

    public getMultiplyShape3D(): IShape3D | undefined {
        if (!isMultiplyModel(this.model)) {
            return undefined;
        }
        if (this.model.selectedModels.length <= 1) {
            return this.model.selectedModels[0]?.shape3d;
        }
        const result = this.model.selectedModels.reduce((
            multiplyShape3d,
            child,
        ) => mergeObjects(multiplyShape3d, child.shape3d), this.model.selectedModels[0].shape3d);
        return result;
    }

    public getGroupShape3D(): IShape3D | undefined {
        const { model } = this;
        if (!isGroupModel(model)) {
            return undefined;
        }
        const children: IElementBaseModel[] = [];
        enumerateGroupItems(model, (childModel: IElementBaseModel) => {
            children.push(childModel);
        });
        if (children.length <= 1) {
            return children[0]?.shape3d;
        }
        const result = children.reduce((
            groupShape3d,
            child,
        ) => mergeObjects(groupShape3d, child.shape3d), children[0].shape3d);
        return result;
    }

    public getElementBaseScene3D(): IScene3D | undefined {
        let curModel: any = this.model.parent;
        while (curModel) {
            if (curModel.type === UIModelType.GROUP && curModel.scene3d) {
                return curModel.scene3d;
            }
            curModel = curModel.parent;
        }
        return undefined;
    }

    private handleMultiplyModel = (
        applyFunc: (model: IElementBaseModel, ...args: any[]) => void,
        ...args: any[]
    ): void => {
        const { model } = this;
        if (isMultiplyModel(model)) {
            transaction(() => {
                model.selectedModels.forEach(child => applyFunc.call(this, child, ...args));
            });
        } else {
            applyFunc.call(this, model, ...args);
        }
    };

    private resetChild3dProperties = (model: IElementBaseModel): void => {
        if (!this.canModelHaveScene3dProperty(model) || !model.sdkData) {
            return;
        }
        model.scene3d = undefined;
        model.shape3d = undefined;
        transaction(() => {
            model.updateAndRemoveProps({}, { scene3d: null, shape3d: null });
        });
    };

    private updateChildScene3D = (model: IElementBaseModel, scene3d: Partial<IScene3D> | undefined): void => {
        // scene3d 有两个主要属性：lightRig 和 camera。这两个必须共存，如果只存在其中一个则导出会报数据修复。
        // 这里在数据更新之前先判断是否只有其中一个，如果是的话则给另一个赋默认值
        if (scene3d) {
            if (!scene3d.camera && scene3d.lightRig) {
                scene3d.camera = {
                    preset: 'orthographicFront',
                };
            }
            if (!scene3d.lightRig && scene3d.camera) {
                scene3d.lightRig = {
                    direction: 't',
                    rigPreset: 'threePt',
                };
            }
        }
        // 针对组合场景，model 数据更新有两种情况：
        // 一种是 model 本身不是 group，则需要向上遍历找到自己是否属于某个 group。如果属于，且该 group 有 scene3d 属性，则更新
        // group 的 scene3d 属性。如果有 group 但是 group 上没有 scene3d，则更新自己的 scene3d
        // 另一种情况是传入 model 为 group。则需要遍历 group 下所有的子元素，将 scene3d 字段删掉，然后单独为 group 写上 scene3d 属性。

        // 临时变量存储待数据更新的 model
        let updatingModel = model;
        // 待删除 scene3d 属性的 model 数组
        const deletingScene3dModels: IElementBaseModel[] = [];
        if (isGroupModel(model)) {
            enumerateGroupItems(model, (childModel: IElementBaseModel) => {
                if (childModel.scene3d) {
                    deletingScene3dModels.push(childModel);
                }
            });
        } else {
            enumerateGroup(model, (groupModel: IGroupModel) => {
                if (groupModel.scene3d) {
                    updatingModel = groupModel;
                    return true;
                }
            });
        }
        transaction(() => {
            if (deletingScene3dModels.length !== 0) {
                deletingScene3dModels.forEach((childModel: IElementBaseModel) => {
                    childModel.updateAndRemoveProps({}, { scene3d: null });
                });
            }
            updatingModel.updateProps({ scene3d });
        });
    };

    private updateChildShape3D = (model: IElementBaseModel, shape3d: Partial<IShape3D> | undefined): void => {
        transaction(() => {
            model.shape3d = shape3d;
            let sdkShape3d: ICTShape3DDelta | null = null;
            if (shape3d) {
                sdkShape3d = {};
                Element3DConverter.convertShape3DFromUiToSdk(shape3d, sdkShape3d ?? undefined);
            }
            (model.sdkData as IDrawingShapeDataModel).api.updateProps({ spPr: { sp3d: sdkShape3d } });
        });
    };

    private updateChildExtrusionHeight = (model: IElementBaseModel, extrusionHeight: number): void => {
        if (!model.sdkData) {
            return;
        }
        const updateShapeExtrusionHeight = (model: IShapeBaseModel): void => {
            if (!model.shape3d) {
                model.shape3d = {};
            }
            model.shape3d.extrusionHeight = extrusionHeight;
            this.updateChildShape3D(model, model.shape3d);
        };
        this.updateModelShape3dProperties(model, updateShapeExtrusionHeight);
    };

    private updateChildExtrusionColor(model: IElementBaseModel, color: string): void {
        if (!model.sdkData) {
            return;
        }
        const updateShapeExtrusionColor = (model: IShapeBaseModel): void => {
            if (!model.shape3d) {
                model.shape3d = {};
            }
            if (!model.shape3d.extrusionColor) {
                model.shape3d.extrusionColor = new TDColor({});
            }
            model.shape3d.extrusionColor?.updateCustomColor(color);
            this.updateChildShape3D(model, model.shape3d);
        };
        this.updateModelShape3dProperties(model, updateShapeExtrusionColor);
    }

    private updateChildIsFlatText = (model: IElementBaseModel, isFlatText: boolean): void => {
        if (!model?.sdkData) {
            return;
        }
        if (isGroupModel(model)) {
            transaction(() => {
                enumerateGroupItems(model, (childModel) => {
                    if (isShapeModel(childModel)) {
                        childModel.updateShowFlatText(isFlatText);
                    }
                });
            });
        } else if (isShapeModel(model)) {
            model.updateShowFlatText(isFlatText);
        }
    };

    private updateChildCameraRotation = (model: IElementBaseModel, rotation: Partial<IRotation3D>): void => {
        if (!model.sdkData || !this.canModelHaveScene3dProperty(model)) {
            return;
        }
        const updateModelCameraRotation = (model: IModelWithScene3d) => {
            if (!model.scene3d) {
                model.scene3d = {};
            }
            if (model.scene3d.camera) {
                model.scene3d.camera.preset = 'orthographicFront';
                model.scene3d.camera.rotation = { x: 0, y: 0, z: 0, ...rotation };
            } else {
                model.scene3d = {
                    camera: {
                        preset: 'orthographicFront', // 不给默认值会导出报修复
                        rotation: {
                            x: 0,
                            y: 0,
                            z: 0,
                            ...rotation,
                        },
                    },
                };
            }
            this.updateChildScene3D(model, model.scene3d);
        };
        if (isGroupModel(model)) {
            const children = recurseChildModels(model, false);
            transaction(() => {
                children.forEach((model: any) => {
                    if (model.scene3d) {
                        model.scene3d = undefined;
                        model.sdkData?.removeProps?.({ scene3d: null });
                    }
                });
                updateModelCameraRotation(model);
            });
        } else {
            let group: IGroupModel | null = null;
            enumerateGroup(model, (groupModel) => {
                if (groupModel.scene3d) {
                    group = groupModel;
                    return true;
                }
            });
            updateModelCameraRotation(group ? group : model);
        }
    };

    private updateChildLightRotation = (model: IElementBaseModel, rotation: Partial<IRotation3D>): void => {
        if (!model.sdkData || !this.canModelHaveScene3dProperty(model)) {
            return;
        }
        if (!model.scene3d) {
            model.scene3d = {};
        }
        if (model.scene3d?.lightRig) {
            model.scene3d.lightRig.rotation = { x: 0, y: 0, z: 0, ...rotation };
        } else {
            model.scene3d.lightRig = {
                direction: 't',
                rotation: {
                    x: 0,
                    y: 0,
                    z: 0,
                    ...rotation,
                },
            };
        }
        this.updateChildScene3D(model, model.scene3d);
    };

    private updateChildLightRigPreset(model: IElementBaseModel, rigPreset: LightType): void {
        if (!model.sdkData || !this.canModelHaveScene3dProperty(model)) {
            return;
        }
        if (!model.scene3d) {
            model.scene3d = {};
        }
        if (model.scene3d.lightRig) {
            model.scene3d.lightRig.rigPreset = rigPreset;
        } else {
            model.scene3d.lightRig = { rigPreset, direction: 't' };
        }
        this.updateChildScene3D(model, model.scene3d);
    }

    private updateChildMaterialPreset = (model: IElementBaseModel, materialPreset: MaterialType): void => {
        if (!model.sdkData) {
            return;
        }
        const updateShapeMaterialPreset = (shape: IShapeBaseModel): void => {
            if (!shape.shape3d) {
                shape.shape3d = {};
            }
            shape.shape3d.materialPreset = materialPreset;
            this.updateChildShape3D(model, model.shape3d);
        };
        this.updateModelShape3dProperties(model, updateShapeMaterialPreset);
    };

    private updateChildCameraPreset = (model: IElementBaseModel, cameraPreset: CameraPresetType): void => {
        if (!model.sdkData || !this.canModelHaveScene3dProperty(model)) {
            return;
        }
        if (!model.scene3d) {
            model.scene3d = {};
        }
        if (!model.scene3d.camera) {
            model.scene3d.camera = {};
        }
        if (model.scene3d.camera.rotation) {
            delete model.scene3d.camera.rotation;
        }
        model.scene3d.camera.preset = cameraPreset;
        this.updateChildScene3D(model, model.scene3d);
    };

    private updateChildBevelTop = (model: IElementBaseModel, bevelTop: IBevel3D): void => {
        if (!model.sdkData) {
            return;
        }
        const updateShapeBevelTop = (shape: IShapeBaseModel): void => {
            if (!shape.shape3d) {
                shape.shape3d = {};
            }
            shape.shape3d.bevelTop = bevelTop;
            // const sdkData = shape.sdkData as Shape;
            // const height = bevelTop.height ? ptToEmu(bevelTop.height) : undefined;
            // const width = bevelTop.width ? ptToEmu(bevelTop.width) : undefined;
            // sdkData.updateBevelTop(height, width);
        };
        this.updateModelShape3dProperties(model, updateShapeBevelTop);
    };

    private updateChildBevelBottom = (model: IElementBaseModel, bevelBottom: IBevel3D): void => {
        if (!model.sdkData) {
            return;
        }
        const updateBevelBottom = (shape: IShapeBaseModel): void => {
            if (!shape.shape3d) {
                shape.shape3d = {};
            }
            shape.shape3d.bevelTop = bevelBottom;
            // const sdkData = shape.sdkData as Shape;
            // const height = bevelBottom.height ? ptToEmu(bevelBottom.height) : undefined;
            // const width = bevelBottom.width ? ptToEmu(bevelBottom.width) : undefined;
            // sdkData.updateBevelBottom(height, width);
        };
        this.updateModelShape3dProperties(model, updateBevelBottom);
    };

    private updateModelShape3dProperties(
        model: IElementBaseModel,
        updateFunc: (model: IElementBaseModel) => void,
    ): void {
        if (isGroupModel(model)) {
            transaction(() => {
                enumerateGroupItems(model, updateFunc);
            });
        } else {
            updateFunc(model);
        }
    }

    private canModelHaveScene3dProperty(model: IElementBaseModel): model is IModelWithScene3d {
        return isGroupModel(model) || isShapeBaseModel(model) || isPictureModel(model);
    }
}
